// FILE: src/services/certBankLoader.js
//
// Lazy certification-bank loader (foundation — PR 1 of the lazy-loading work).
//
// PUBLIC API: ensureCertLoaded(certificationId) -> Promise<CertBankResult>
//
// This module is the loading + caching layer. It is NOT yet wired into the app
// (App.js still eagerly prefetches via questionService); wiring + removing the
// static bank imports is PR 2. Therefore PR 1 is behaviour-neutral and does NOT
// reduce the production bundle — the static imports in questionService.js keep
// the banks in the initial chunk until PR 2 removes them. PR 1's acceptance
// criterion is a tested loading foundation with no runtime behaviour change.
//
// SOURCE PRECEDENCE (preserves today's behaviour — see docs/lazy-loading-investigation.md):
//   1. Load the selected certification from Supabase (cert-scoped query:
//      loadAllQuestionsFromSupabase uses `.eq('exam_id', certId)` server-side).
//   2. Classify the outcome:
//        • supabase-success — >0 active (non-retired) questions returned
//        • supabase-empty   — valid response, but 0 active questions
//        • supabase-error   — request failed (null / threw)
//   3. On supabase-empty OR supabase-error, dynamically import ONLY this cert's
//      bundled fallback (never in parallel with a successful Supabase load),
//      filtered by the same active/non-retired rules (normalizeBundledBank).
//   4. If the bundled fallback is also empty -> a deliberate `empty` result
//      (no retry/loop).
//
//   COMPATIBILITY POLICY: a valid empty Supabase response is NOT treated as a
//   technical error; we fall back to bundled to preserve the current
//   learner-visible behaviour (the eager store only populated when >0). Whether
//   an intentionally-empty Supabase bank should become authoritative is a
//   separate product decision for after the loading migration is stable.
//
// CACHING:
//   • A bank with >0 usable questions is cached for the session — including a
//     successful bundled fallback after a Supabase failure (that transient
//     Supabase failure is therefore NOT re-queried once usable questions exist).
//   • A result with NO usable questions is NOT cached, so it is retried on the
//     next call this session (a bank still being built, or a transient outage
//     that later recovers). Likewise a load that throws (no usable bank produced)
//     is removed from the in-flight cache and not stored, so the next call retries.
//   • The in-flight slot is always cleared in `finally` (success AND failure).
//   • Concurrent calls for the same cert share one in-flight promise (one
//     network request, one load operation). No browser-storage persistence.
//
// No Redux. Pure async helpers.

import { loadAllQuestionsFromSupabase, loadQuestionPoolFromSupabase, normalizeBundledBank } from './questionService';
import { getActiveCertIds } from '../config/exams';

/**
 * Explicit loader map: certificationId -> () => dynamic import of its bundled
 * fallback bank. Explicit (not a path built from an arbitrary string) so the
 * set of code-split chunks is statically analyzable by the bundler. Add a new
 * certification here AND to config/exams; the coverage test cross-checks the two.
 */
export const bundledBankLoaders = {
  pmp:         () => import('../data/pmp/pmp-question-bank.app.v1.2.json'),
  cpmai:       () => import('../data/cpmai/cpmai-question-bank.app.v1.0.json'),
  'pmi-acp':   () => import('../data/pmiacp/pmiacp-question-bank.app.v1.0.json'),
  'pmi-pmocp': () => import('../data/pmipmocp/pmipmocp-question-bank.app.v1.0.json'),
};

// Session caches (in-memory only).
const resolvedCache = new Map(); // certId -> CertBankResult (incl. empty + bundled-fallback)
const inflightCache = new Map(); // certId -> Promise<CertBankResult>
const bankGeneration = new Map(); // certId -> bumped by invalidateCertBank

function isSupportedCert(certId) {
  return typeof certId === 'string'
    && certId.length > 0
    && Object.prototype.hasOwnProperty.call(bundledBankLoaders, certId);
}

// Supabase's loadAllQuestionsFromSupabase already type-filters via the query;
// apply the non-retired rule here to match the learner path.
function activeNonRetired(questions) {
  return Array.isArray(questions) ? questions.filter((q) => q && q.status !== 'retired') : [];
}

async function loadBundledFallback(certId, fallbackReason, supabaseStatus) {
  const raw = await bundledBankLoaders[certId](); // only the selected cert's chunk
  const questions = normalizeBundledBank(raw, certId); // handles array | {default}; throws on malformed
  // Activation reliability (2026-08-12): an EMPTY bundled fallback must never
  // resolve as a successful bank load. Resolving one let a failed/empty
  // Supabase load silently produce a zero-question "completed" session (the
  // Aug 10 pmi-pmocp dormant-user failure). Throwing makes the coordinator
  // surface its retryable error state instead, and nothing is cached, so a
  // later attempt (or Retry) refetches.
  if (questions.length === 0) {
    const err = new Error(
      supabaseStatus === 'supabase-error'
        ? 'Could not load questions for this certification. Please try again.'
        : 'No questions are available for this certification yet.'
    );
    err.code = 'EMPTY_QUESTION_BANK';
    err.certificationId = certId;
    err.supabaseStatus = supabaseStatus;
    throw err;
  }
  return {
    certificationId: certId,
    questions,
    source: 'bundled',
    status: supabaseStatus,        // 'supabase-empty' | 'supabase-error'
    usedFallback: true,
    fallbackReason,                // 'empty' | 'error' — distinguishable for telemetry
    empty: false,                  // empty fallbacks throw above (never resolve)
  };
}

async function loadCertBank(certId) {
  let supa;
  try {
    // Learner-path label: keeps learner bank loads separable from admin
    // tooling loads ('questions_load_all') in the Slow-queries drill-down.
    supa = await loadAllQuestionsFromSupabase(certId, { timingLabel: 'questions_load_bank' });
  } catch (_) {
    supa = null; // a throw is treated as supabase-error
  }

  if (supa === null) {
    return loadBundledFallback(certId, 'error', 'supabase-error');
  }

  const active = activeNonRetired(supa);
  if (active.length > 0) {
    return {
      certificationId: certId,
      questions: active,
      source: 'supabase',
      status: 'supabase-success',
      usedFallback: false,
      fallbackReason: null,
      empty: false,
    };
  }

  // supabase-empty: valid response, 0 active -> bundled fallback (compatibility policy)
  return loadBundledFallback(certId, 'empty', 'supabase-empty');
}

/**
 * ensureCertLoaded — load (or return cached) the question bank for one cert.
 * @param {string} certificationId
 * @returns {Promise<CertBankResult>}
 */
export function ensureCertLoaded(certificationId) {
  if (!isSupportedCert(certificationId)) {
    return Promise.reject(
      new Error(`[certBankLoader] unsupported certification id: ${JSON.stringify(certificationId)}`)
    );
  }
  if (resolvedCache.has(certificationId)) {
    return Promise.resolve(resolvedCache.get(certificationId));
  }
  if (inflightCache.has(certificationId)) {
    return inflightCache.get(certificationId);
  }
  const generation = bankGeneration.get(certificationId) || 0;
  const promise = loadCertBank(certificationId)
    .then((result) => {
      // A load that started before invalidateCertBank() may predate a write
      // (e.g. a bulk import); return it to its caller but never cache it.
      if ((bankGeneration.get(certificationId) || 0) !== generation) return result;
      // Cache only a bank that actually has usable questions. A result with no
      // usable questions (empty from BOTH Supabase and the bundled fallback, or
      // a load that produced none) is intentionally NOT cached, so it can be
      // retried later in the same browser session — e.g. a bank still being
      // built, or a transient outage that later recovers.
      if (result.questions.length > 0) {
        resolvedCache.set(certificationId, result);
      }
      return result;
    })
    .finally(() => {
      // Always clear the in-flight slot — on success AND failure — so a settled
      // (resolved or rejected) promise can never remain stuck.
      if (inflightCache.get(certificationId) === promise) inflightCache.delete(certificationId);
    });
  inflightCache.set(certificationId, promise);
  return promise;
}

// ── Metadata pool (session-draw stage 3, PR 1) ───────────────────────────────
//
// ensureCertPool loads the lightweight selection-metadata pool for one cert.
// It is the future input to the learner session DRAW (gated per cert by
// featureFlags.isMetadataPoolEnabled in the hydration PR); nothing routes
// through it yet — PR 1 is preparatory and behaviour-neutral.
//
// CONTRACT:
//   • Results are poolOnly — they must ONLY ever be stored in App's separate
//     `metadataPools` slot. A poolOnly result must NEVER be written into
//     `questionStores` (the dual-use full-content store shared with the
//     Question Editor / Bulk Import / preview / session patching) and must
//     never reach a rendered session: pool rows carry no question content
//     (`question`/`choices`/`explanation` are absent).
//   • Caches are fully independent of the bank caches: a bank load never
//     satisfies a pool request and vice versa.
//   • Same fallback semantics as the bank path: on supabase-error or
//     supabase-empty, fall back to the cert's bundled bank. Bundled rows are
//     full-content (local JSON, zero network cost) — incidental, and keeps
//     bundled-JSON behaviour unchanged; the result is still flagged poolOnly.

const poolResolvedCache = new Map(); // certId -> pool CertBankResult
const poolInflightCache = new Map(); // certId -> Promise<pool CertBankResult>

async function loadCertPool(certId) {
  let supa;
  try {
    // Emits telemetry under 'questions_draw_pool' (never 'questions_load_bank').
    supa = await loadQuestionPoolFromSupabase(certId);
  } catch (_) {
    supa = null; // a throw is treated as supabase-error
  }

  if (supa === null) {
    return { ...(await loadBundledFallback(certId, 'error', 'supabase-error')), poolOnly: true };
  }

  const active = activeNonRetired(supa);
  if (active.length > 0) {
    return {
      certificationId: certId,
      questions: active,
      source: 'supabase-pool',
      status: 'supabase-success',
      usedFallback: false,
      fallbackReason: null,
      empty: false,
      poolOnly: true,
    };
  }

  // supabase-empty: valid response, 0 active -> bundled fallback (compatibility policy)
  return { ...(await loadBundledFallback(certId, 'empty', 'supabase-empty')), poolOnly: true };
}

/**
 * ensureCertPool — load (or return cached) the metadata pool for one cert.
 * Same caching rules as ensureCertLoaded (cache only >0 usable questions;
 * shared in-flight promise; in-flight slot always cleared), but against
 * separate caches.
 * @param {string} certificationId
 * @returns {Promise<CertBankResult & {poolOnly: true}>}
 */
export function ensureCertPool(certificationId) {
  if (!isSupportedCert(certificationId)) {
    return Promise.reject(
      new Error(`[certBankLoader] unsupported certification id: ${JSON.stringify(certificationId)}`)
    );
  }
  if (poolResolvedCache.has(certificationId)) {
    return Promise.resolve(poolResolvedCache.get(certificationId));
  }
  if (poolInflightCache.has(certificationId)) {
    return poolInflightCache.get(certificationId);
  }
  const promise = loadCertPool(certificationId)
    .then((result) => {
      if (result.questions.length > 0) {
        poolResolvedCache.set(certificationId, result);
      }
      return result;
    })
    .finally(() => {
      poolInflightCache.delete(certificationId);
    });
  poolInflightCache.set(certificationId, promise);
  return promise;
}

/**
 * invalidateCertBank — drop one cert's cached full bank (and any in-flight
 * load) so the next ensureCertLoaded refetches. Called after an admin write
 * that the cached bank cannot contain, e.g. a bulk import: Bulk Import loads
 * the bank for conflict detection BEFORE saving, so without this the Question
 * Editor would later be served that pre-import bank.
 * @param {string} certificationId
 */
export function invalidateCertBank(certificationId) {
  bankGeneration.set(certificationId, (bankGeneration.get(certificationId) || 0) + 1);
  resolvedCache.delete(certificationId);
  inflightCache.delete(certificationId);
}

/**
 * invalidateCertPool — drop one cert's cached metadata pool so the next
 * ensureCertPool refetches. Used when hydration discovers the cached pool is
 * stale (a drawn id no longer exists), making the learner's Retry meaningful.
 * @param {string} certificationId
 */
export function invalidateCertPool(certificationId) {
  poolResolvedCache.delete(certificationId);
}

/** Test-only: clear the metadata-pool caches (bank caches untouched). */
export function __resetCertPoolCache() {
  poolResolvedCache.clear();
  poolInflightCache.clear();
}

/**
 * loadAllCertBanks — explicit, on-demand load of every active cert's bank.
 * For the rare admin workflows that genuinely need all banks. This is NOT a
 * restored global startup prefetch; nothing calls it on app mount.
 * @returns {Promise<Object<string, CertBankResult>>}
 */
export function loadAllCertBanks() {
  const ids = getActiveCertIds().filter(isSupportedCert);
  return Promise.all(ids.map((id) => ensureCertLoaded(id))).then((banks) => {
    const out = {};
    ids.forEach((id, i) => { out[id] = banks[i]; });
    return out;
  });
}

/**
 * loadBundledCertBank — async, bundled-ONLY load of one cert's question bank.
 *
 * Dynamically imports the cert's code-split bundled JSON and normalizes it the
 * same way the learner path does (type filter + non-retired + normalizeQuestion).
 * Unlike ensureCertLoaded it never touches Supabase — it is the explicit bundled
 * fallback for consumers that have already decided to use bundled data (e.g. the
 * admin dashboard's offline/empty-Supabase fallback). Crucially it depends only
 * on the DYNAMIC import map, NOT on questionService's static RAW_QUESTION_BANKS,
 * so it survives PR 2C's removal of the static imports.
 *
 * @param {string} certId
 * @returns {Promise<any[]>} normalized questions ([] is possible)
 */
export async function loadBundledCertBank(certId) {
  if (!isSupportedCert(certId)) {
    throw new Error(`[certBankLoader] unsupported certification id: ${JSON.stringify(certId)}`);
  }
  const raw = await bundledBankLoaders[certId]();
  return normalizeBundledBank(raw, certId);
}

/** Test-only: clear the in-memory caches. */
export function __resetCertBankCache() {
  resolvedCache.clear();
  inflightCache.clear();
}
